import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { videoService } from "@/services/videos";
import {
  ArrowLeft,
  User,
  Mail,
  Calendar,
  LogOut,
  Video,
  ShoppingBag,
  Receipt,
  Pencil,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Transaction, transactionService } from "@/services/transactions";
import Skeleton from "react-loading-skeleton";

interface Video {
  cart_items: any;
  videos: any;
  video: any;
  id: string;
  court_id: string;
  storage_url: string;
  watermarked_url: string;
  watch_count: number;
  price: number;
  purchase_count: boolean;
  download_count: number;
  created_at: string;
  updated_at: string;
  // Used for user library
  user_id: string;
  clip_id: string;
  bought: string;
  merchant_name: string;
  merchant_address: string;
  court_name: string;
  court_location: string;
  // Add other Video properties as needed
}

const Profile = () => {
  const navigate = useNavigate();
  const [videoLibrary, setVideos] = useState<Video[]>([]);
  const [ownedVideos, setOwnedVideos] = useState<Video[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const { user, logout } = useAuth();

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    const getTotalTransactions = async () => {
      try {
        const response = await transactionService.fetchTransactionsByUserId();
        setTransactions(response.transactions || []);
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
      } finally {
        setLoadingTransactions(false);
      }
    };
    getTotalTransactions();
  }, []);

  useEffect(() => {
    const getRecentVideos = async () => {
      try {
        const response = await videoService.fetchUsersVideoLibrary();

        const usersVideos = response.cart_items
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 3)
          .map((video) => ({
            id: video.id,
            user_id: video.user_id,
            clip_id: video.clip_id,
            bought: video.bought,
            created_at: video.created_at,
            updated_at: video.updated_at,
            storage_url: video.storage_url,
            watermarked_url: video.watermarked_url,
            price: video.price,
            watch_count: video.watch_count,
            purchase_count: video.purchase_count,
            download_count: video.download_count,
            merchant_name: video.merchant_name,
            merchant_address: video.merchant_address,
            court_name: video.court_name,
            court_location: video.court_location,
          }));
        const ownedVideosRes = response.cart_items
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          // .slice(0, 3)
          .map((video) => ({
            id: video.id,
            user_id: video.user_id,
            clip_id: video.clip_id,
            bought: video.bought,
            created_at: video.created_at,
            updated_at: video.updated_at,
            storage_url: video.storage_url,
            watermarked_url: video.watermarked_url,
            price: video.price,
            watch_count: video.watch_count,
            purchase_count: video.purchase_count,
            download_count: video.download_count,
            merchant_name: video.merchant_name,
            merchant_address: video.merchant_address,
            court_name: video.court_name,
            court_location: video.court_location,
          }));
        setOwnedVideos(ownedVideosRes);
        setVideos(usersVideos);
      } catch (error) {
        console.error("Failed to fetch videos:", error);
      } finally {
        setLoadingVideos(false);
      }
    };
    getRecentVideos();
  }, []);

  useEffect(() => {
    if (user) setLoadingProfile(false);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100">
      {/* Header */}
      <header className="pt-6 pb-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4 px-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/brands")}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              My Profile
            </h1>
            <p className="text-muted-foreground text-sm">
              Manage your account & activity
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className=" max-w-4xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white/80 backdrop-blur border rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="w-5 h-5 text-primary" />
                  Account Information
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Avatar */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-xl font-bold text-primary">
                    {loadingProfile ? <Skeleton circle width={64} height={64} /> : user?.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{loadingProfile ? <Skeleton width={140} /> : user?.name}</p>
                    <p className="text-sm text-muted-foreground">{loadingProfile ? <Skeleton width={180} /> : user?.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="font-medium">{loadingProfile ? <Skeleton width={120} /> : user?.phone}</p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Member Since</p>
                    <p className="font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {loadingProfile ? <Skeleton width={140} /> : new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Stats */}
            <Card className="bg-white/80 backdrop-blur border rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle>Account Activity</CardTitle>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-2 gap-6">

                  <div className="rounded-xl bg-primary/5 p-6 text-center">
                    <div className="text-3xl font-bold text-primary mb-2">
                      {loadingVideos ? <Skeleton width={40} /> : ownedVideos.length}
                    </div>
                    <div className="text-sm text-muted-foreground flex justify-center gap-1">
                      <Video className="w-4 h-4" />
                      Videos Owned
                    </div>
                  </div>

                  <div className="rounded-xl bg-green-500/5 p-6 text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {loadingTransactions ? (
                        <Skeleton width={40} />
                      ) : (
                        transactions.filter((t) => !!t.paid_at).length
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground flex justify-center gap-1">
                      <ShoppingBag className="w-4 h-4" />
                      Transactions
                    </div>
                  </div>

                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <Card className="bg-white/80 backdrop-blur border rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>

              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start hover:shadow-sm"
                  onClick={() => navigate("/library")}
                >
                  <Video className="w-4 h-4 mr-2" />
                  My Video Library
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start hover:shadow-sm"
                  onClick={() => navigate("/brands")}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Browse Courts
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start hover:shadow-sm"
                  onClick={() => navigate("/transaction-history")}
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Transaction History
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur border border-red-200 rounded-2xl shadow-sm">
              <CardHeader>
                <CardTitle className="text-red-600">
                  Account Actions
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/profile/edit")}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>

                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Activity */}
        <Card className="mt-10 bg-white/80 backdrop-blur border rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle>Recent Videos</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            {loadingVideos
              ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/40"
                >
                  <div className="space-y-2">
                    <Skeleton width={220} height={16} />
                    <Skeleton width={140} height={12} />
                  </div>
                  <Skeleton width={70} height={32} />
                </div>
              ))
              : videoLibrary.slice(0, 3).map((video) => (
                <div
                  key={video.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/40 hover:bg-muted transition"
                >
                  <div>
                    <p className="font-medium">
                      {video.court_name} • {video.merchant_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {video.created_at}
                    </p>
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/videos/${video.clip_id}`)}
                  >
                    Watch
                  </Button>
                </div>
              ))}

            {videoLibrary.length > 3 && (
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => navigate("/library")}
              >
                View All Videos
              </Button>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Profile;
